/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import dayjs, { Dayjs } from 'dayjs'

import { protectedResources } from '../authConfig'
import { msalInstance } from '../index'
import { AccountInfo, SilentRequest } from '@azure/msal-browser'
import { CompilationContainer, CompilationContainerModification } from '../model/CompilationContainer'
import { Template, TemplateDetails } from '../model/Template'
import { Answer } from '../model/Answer'
import { Graph, GraphType } from '../model/Graph'
import { Summary } from '../model/Summary'
import { Question, SingleChoiceQuestion } from '../model/Question'

const getToken = async (): Promise<string> => {
  const account: AccountInfo | null = msalInstance.getActiveAccount()

  if (!account) {
    throw new Error('No active account! Verify a user has been signed in and setActiveAccount has been called.')
  }

  const silentRequest: SilentRequest = {
    account: account,
    scopes: protectedResources.apiPortal.scopes,
  }

  try {
    const response = await msalInstance.acquireTokenSilent(silentRequest)
    return response.accessToken
  } catch (error) {
    console.error('Error acquiring token silently', error)
    throw new Error('Failed to acquire token silently. Check the console for more details.')
  }
}

// export default getToken;

// Survey

export type CreateCompilationContainerData = {
  templateId: string
  allowedUsers: string[]
  type: 'DEFAULT' | 'ANONYMOUS'
  beginningDate: Dayjs
  endingDate: Dayjs
}

// /// Compilation service

export async function getAvailableSurveys(): Promise<CompilationContainer[]> {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`

  headers.append('Authorization', bearer)
  headers.append('Content-Type', 'application/json')

  // istanza di URLSearchParams
  const params = new URLSearchParams()
  // uso il metodo set come prima per aggiungere parametri
  params.set('searchType', 'owned')
  // params.set("searchType", "available");

  const options = {
    method: 'GET',
    headers: headers,
  }

  return fetch(protectedResources.apiPortal.compilationsEndpoint + `?${params.toString()}`, options)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.json()
    })
    .then((data) => data as CompilationContainer[])
    .catch((error) => {
      console.log(error)
      return []
    })
}

export async function getCompilationContainers(): Promise<CompilationContainer[]> {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`

  headers.append('Authorization', bearer)
  headers.append('Content-Type', 'application/json')

  const options = {
    method: 'GET',
    headers: headers,
  }

  return fetch(protectedResources.apiPortal.compilationsEndpoint + '/containers', options)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.json()
    })
    .then((data) => data as CompilationContainer[])
    .catch((error) => {
      console.log(error)
      return []
    })
}

export async function createCompilationContainer(data: CreateCompilationContainerData): Promise<CompilationContainer> {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`
  headers.append('Authorization', bearer)
  headers.append('Content-Type', 'application/json')

  // const params = new URLSearchParams({ owner: owner });

  const options = {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(data),
  }

  return fetch(protectedResources.apiPortal.compilationsEndpoint + '/containers', options)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.json()
    })
    .then((data) => data as CompilationContainer)
    .catch((error) => {
      console.error(error)
      throw new Error('Error creating compilation container')
    })
}

export async function getCompilationContainerTemplate(id: string): Promise<TemplateDetails> {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`
  headers.append('Authorization', bearer)
  headers.append('Content-Type', 'application/json')

  const options = {
    method: 'GET',
    headers: headers,
  }

  return fetch(protectedResources.apiPortal.compilationsEndpoint + `/${id}/template`, options)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.json()
    })
    .then((data) => data as TemplateDetails)
    .catch((error) => {
      console.error(error)
      throw new Error('Error fetching compilation container template')
    })
}

export async function deleteCompilationContainer(id: string) {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`

  headers.append('Authorization', bearer)

  const options = {
    method: 'DELETE',
    headers: headers,
  }

  try {
    const response = await fetch(protectedResources.apiPortal.compilationsEndpoint + `/${id}`, options)
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText)
    }
    return response.statusText
  } catch (error) {
    console.error(error)
    throw error
  }
}

export async function setAllowedUsers(compilationContainerId: string, userIds: string[]): Promise<void> {
  const accessToken = await getToken()

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${accessToken}`)
  headers.append('Content-Type', 'application/json')

  const options = {
    method: 'PUT',
    headers: headers,
    body: JSON.stringify(userIds),
  }

  try {
    const response = await fetch(
      protectedResources.apiPortal.compilationsEndpoint + `/${compilationContainerId}/allowed-users`,
      options
    )
    if (!response.ok) {
      const errorBody = await response.json()
      const errorMessage = errorBody.error || 'Errore sconosciuto dal backend'
      throw new Error(errorMessage)
    }
  } catch (error) {
    // console.error(error);
    throw error
  }
}

export async function updateCompilation(
  compilationContainerId: string,
  containerModification: CompilationContainerModification
): Promise<void> {
  const accessToken = await getToken()

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${accessToken}`)
  headers.append('Content-Type', 'application/json')

  const options = {
    method: 'PUT',
    headers: headers,
    body: JSON.stringify(containerModification),
  }

  return fetch(protectedResources.apiPortal.compilationsEndpoint + `/${compilationContainerId}`, options)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
    })
    .catch((error) => {
      console.error(error)
      throw new Error(`Error updating compilation container ${compilationContainerId}`)
    })
}

export async function removeAllowedUser(compilationContainerId: string, userId: string): Promise<void> {
  const accessToken = await getToken()

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${accessToken}`)

  const params = new URLSearchParams({ userId: userId })

  const options = {
    method: 'DELETE',
    headers: headers,
  }

  return fetch(
    protectedResources.apiPortal.compilationsEndpoint + `/${compilationContainerId}/allowed-users?${params.toString()}`,
    options
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
    })
    .catch((error) => {
      console.error(error)
      throw new Error(`Error removing allowed user ${userId} for compilation container ${compilationContainerId}`)
    })
}

export async function addCompilation(compilationContainerId: string, answers: Answer[]) {
  const accessToken = await getToken()

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${accessToken}`)
  headers.append('Content-Type', 'application/json')

  const compilation = {
    '@type': 'DefaultCompilation',
    answers: answers,
  }

  const options = {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(compilation),
  }

  const response = await fetch(
    protectedResources.apiPortal.compilationsEndpoint + `/${compilationContainerId}/compilations`,
    options
  )
  if (!response.ok) {
    throw new Error('Network response was not ok')
  }
  return await response.json()
}

export async function getGraphs(compilationContainerId: string, graphType: GraphType) {
  const accessToken = await getToken()

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${accessToken}`)

  // const params = new URLSearchParams({ graphType: graphType });

  const params = new URLSearchParams()
  params.set('graphType', graphType)

  const options = {
    method: 'GET',
    headers: headers,
  }

  const response = await fetch(
    protectedResources.apiPortal.compilationsEndpoint + `/${compilationContainerId}/graphs?${params.toString()}`,
    options
  )
  if (!response.ok) {
    throw new Error('Network response was not ok')
  }
  return (await response.json()) as Graph[]
}

export async function getCompilationHistory(compilationContainerId: string): Promise<Map<string, Dayjs>> {
  const accessToken = await getToken()

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${accessToken}`)

  const options = {
    method: 'GET',
    headers: headers,
  }

  const response = await fetch(
    protectedResources.apiPortal.compilationsEndpoint + `/${compilationContainerId}/compilation-history`,
    options
  )
  if (!response.ok) {
    throw new Error('Network response was not ok')
  }

  const json = await response.json()
  const map = new Map<string, Dayjs>()
  for (const key in json) {
    map.set(key, dayjs(json[key]))
  }
  return map
}

export async function getSummary(compilationContainerId: string): Promise<Summary[]> {
  const accessToken = await getToken()

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${accessToken}`)

  const options = {
    method: 'GET',
    headers: headers,
  }

  const response = await fetch(
    protectedResources.apiPortal.compilationsEndpoint + `/${compilationContainerId}/summaries`,
    options
  )
  if (!response.ok) {
    throw new Error('Network response was not ok')
  }
  return await response.json()
}

// /// Template service

export async function getTemplates(): Promise<Template[]> {
  const accessToken = await getToken()

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${accessToken}`)

  const options = {
    method: 'GET',
    headers: headers,
  }

  const response = await fetch(protectedResources.apiPortal.templatesEndpoint, options)
  if (!response.ok) {
    throw new Error('Network response was not ok')
  }
  return await response.json()
}

export async function createTemplate(
  title: string,
  description: string,
  referencePeriod: string,
  owner: string,
  questions?: Question[]
): Promise<Template> {
  const accessToken = await getToken()

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${accessToken}`)
  headers.append('Content-Type', 'application/json')

  const data = {
    title,
    description,
    referencePeriod,
    owner,
    questions: questions ? initChoiceSelectionTypes(questions) : [],
  }

  const options = {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(data),
  }

  try {
    const response = await fetch(protectedResources.apiPortal.templatesEndpoint, options)
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || 'Network response was not ok')
    }
    return await response.json()
  } catch (error) {
    throw error
  }
}

function initChoiceSelectionTypes(questions: Question[]): Question[] {
  questions.forEach((question) => {
    switch (question.questionType) {
      case 'TEXTUAL':
        return question
      case 'NUMERIC':
        return question
      case 'SINGLE_CHOICE': {
        const singleChoiceQuestion = question as SingleChoiceQuestion
        if ((singleChoiceQuestion.choiceSelection.choices as string[]).every((choice) => typeof choice === 'string')) {
          singleChoiceQuestion.choiceSelection.type = 'SIMPLE'
        } else {
          singleChoiceQuestion.choiceSelection.type = 'WEIGHTED'
        }
        return singleChoiceQuestion
      }
      case 'MULTIPLE_CHOICE': {
        const multipleChoiceQuestion = question as SingleChoiceQuestion
        if (
          (multipleChoiceQuestion.choiceSelection.choices as string[]).every((choice) => typeof choice === 'string')
        ) {
          multipleChoiceQuestion.choiceSelection.type = 'SIMPLE'
        } else {
          multipleChoiceQuestion.choiceSelection.type = 'WEIGHTED'
        }
        return multipleChoiceQuestion
      }
    }
  })
  return questions
}

// export async function updateTemplate(templateId: string, newTitle: string, newDescription: string, newReferencePeriod: string): Promise<Template> {
//     const accessToken = await getToken();

//     const headers = new Headers();
//     headers.append('Authorization', `Bearer ${accessToken}`);
//     headers.append('Content-Type', 'application/json');

//     const data = {
//         title: newTitle,
//         description: newDescription,
//         referencePeriod: newReferencePeriod,
//     };

//     const options = {
//         method: 'PUT',
//         headers: headers,
//         body: JSON.stringify(data),
//     };

//     const response = await fetch(protectedResources.apiPortal.templatesEndpoint + `/${templateId}`, options);
//     if (!response.ok) {
//         throw new Error('Network response was not ok');
//     }
//     return await response.json();
// }

export async function updateTemplate(
  templateId: string,
  newTitle: string,
  newDescription: string,
  newReferencePeriod: string
): Promise<Template> {
  const accessToken = await getToken()

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${accessToken}`)
  headers.append('Content-Type', 'application/json')

  const data = {
    title: newTitle,
    description: newDescription,
    referencePeriod: newReferencePeriod,
  }

  const options = {
    method: 'PUT',
    headers: headers,
    body: JSON.stringify(data),
  }

  try {
    const response = await fetch(protectedResources.apiPortal.templatesEndpoint + `/${templateId}`, options)
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || 'Network response was not ok')
    }
    return await response.json()
  } catch (error) {
    throw error
  }
}

export async function deleteTemplate(templateId: string): Promise<string> {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`
  headers.append('Authorization', bearer)

  const options = {
    method: 'DELETE',
    headers: headers,
  }

  const response = await fetch(`${protectedResources.apiPortal.templatesEndpoint}/${templateId}`, options)
  if (!response.ok) {
    throw new Error('Network response was not ok')
  }
  return response.statusText
}

export async function deleteQuestionAtIndex(templateId: string, index: number): Promise<string> {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`
  headers.append('Authorization', bearer)

  const params = new URLSearchParams({ index: index.toString() })

  const options = {
    method: 'DELETE',
    headers: headers,
  }

  const response = await fetch(
    `${protectedResources.apiPortal.templatesEndpoint}/${templateId}/questions?${params.toString()}`,
    options
  )
  // console.log("Response api: ", response);
  if (!response.ok) {
    // console.log("Response not ok");
    const errorText = await response.text()
    throw new Error('Network response was not ok: ' + errorText)
  }
  const responseData = await response.json()
  // console.log("Response data: ", responseData);
  return responseData.message
}

export async function addQuestionToTemplate(templateId: string, question: Question): Promise<void> {
  const accessToken = await getToken()

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${accessToken}`)
  headers.append('Content-Type', 'application/json')

  const params = new URLSearchParams({ index: '-1' })

  const options = {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(question),
  }

  await fetch(
    protectedResources.apiPortal.templatesEndpoint + `/${templateId}/questions?${params.toString()}`,
    options
  ).then((response) => {
    if (!response.ok) {
      throw new Error('Network response was not ok')
    }
  })
}

export async function updateQuestionAtIndex(templateId: string, index: number, question: Question): Promise<boolean> {
  const accessToken = await getToken()

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${accessToken}`)
  headers.append('Content-Type', 'application/json')

  const params = new URLSearchParams({ questionToUpdateIndex: index.toString() })

  const options = {
    method: 'PUT',
    headers: headers,
    body: JSON.stringify(question),
  }

  const response = await fetch(
    protectedResources.apiPortal.templatesEndpoint + `/${templateId}/questions?${params.toString()}`,
    options
  )
  return response.ok
}
